// Admin Settings Hub — RTM Req 52
// Consolidated settings index with role-conditional tiles linking to each
// admin settings section. Tiles are gated by hasPermission() + plan feature
// flags — not raw role-name checks — so the visibility matrix matches the
// actual backend authorization layer.
import { Link } from "wouter";
import { PageShell } from "@/components/page-shell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, useFeature, hasPermission } from "@/lib/auth";
import {
  Building2, Palette, CreditCard, Brain, ShieldCheck, Key,
  BarChart3, Users, Globe, Lock, Bell, Sliders, Code2,
} from "lucide-react";

type Tile = {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  hidden?: boolean;
};

export default function AdminSettingsHub() {
  const { user } = useAuth();

  // Use the permission system (not raw role names) to gate each tile.
  // hasPermission() evaluates the user's actual permission set which is
  // granted per role by the backend RolePermissionSeeder.
  const canBilling       = hasPermission(user, "billing.manage");
  const canAiProviders   = hasPermission(user, "aiproviders.manage");
  const canAudit         = hasPermission(user, "audit.view");
  const canWebhooks      = hasPermission(user, "webhooks.manage");
  const canIntegrations  = hasPermission(user, "integrations.manage");
  const canBranding      = hasPermission(user, "branding.manage");
  const canApiKeys       = hasPermission(user, "api_keys.manage");
  const canSettings      = hasPermission(user, "settings.manage");
  const canGroups        = hasPermission(user, "groups.manage");
  const canSso           = hasPermission(user, "sso.manage");
  const canAdmin         = hasPermission(user, "admin.access");

  // Plan feature flags (shown even without permission so upgrade CTA is visible).
  const hasAiProviders  = useFeature("aiproviders");
  const hasAudit        = useFeature("audit");
  const hasScim         = useFeature("scim");
  const hasSso          = useFeature("sso");
  const hasComplianceF  = useFeature("compliance");
  const hasCustomBrand  = useFeature("custom_branding");

  // Must have at least settings.manage or billing.manage to see this hub at all.
  const isAnyAdmin = canBilling || canAiProviders || canAudit || canSettings ||
                     canGroups || canSso || canBranding || canAdmin || canApiKeys;

  if (!isAnyAdmin) {
    return (
      <PageShell title="Settings">
        <p className="text-sm text-muted-foreground">You don't have permission to view this page.</p>
      </PageShell>
    );
  }

  const tiles: Tile[] = [
    // ── Org settings — settings.manage ───────────────────────────────────────
    {
      title: "General",
      description: "Organisation name, logo, brand colour, and landing page copy.",
      href: "/settings",
      icon: <Building2 className="w-5 h-5" />,
      hidden: !canSettings,
    },
    {
      title: "Branding & Theme",
      description: "Custom colours, typography, and white-label branding.",
      href: "/settings",
      icon: <Palette className="w-5 h-5" />,
      badge: hasCustomBrand ? undefined : "Upgrade",
      hidden: !canSettings,
    },
    // ── Billing — billing.manage ──────────────────────────────────────────────
    {
      title: "Billing & Subscription",
      description: "View your current plan, upgrade, and manage payment details.",
      href: "/billing",
      icon: <CreditCard className="w-5 h-5" />,
      hidden: !canBilling,
    },
    // ── AI providers — aiproviders.manage ─────────────────────────────────────
    {
      title: "AI Model Integration",
      description: "Connect an LLM provider for the Setup Agent and Help Assistant.",
      href: "/ai-providers",
      icon: <Brain className="w-5 h-5" />,
      badge: hasAiProviders ? undefined : "Upgrade",
      hidden: !canAiProviders,
    },
    // ── Compliance — settings.manage + plan ───────────────────────────────────
    {
      title: "Compliance",
      description: "Data retention, PHI safeguards, and inclusive-language policy.",
      href: "/compliance",
      icon: <ShieldCheck className="w-5 h-5" />,
      badge: hasComplianceF ? undefined : "Upgrade",
      hidden: !canSettings,
    },
    // ── Identity — sso.manage ─────────────────────────────────────────────────
    {
      title: "Single Sign-On (SSO)",
      description: "Configure SAML / OAuth identity provider and enforce SSO.",
      href: "/settings/sso",
      icon: <Lock className="w-5 h-5" />,
      badge: hasSso ? undefined : "Upgrade",
      hidden: !canSso,
    },
    {
      title: "SCIM Provisioning",
      description: "Automate user provisioning and deprovisioning via SCIM 2.0.",
      href: "/integrations",
      icon: <Users className="w-5 h-5" />,
      badge: hasScim ? undefined : "Upgrade",
      hidden: !canIntegrations,
    },
    // ── Audit — audit.view ────────────────────────────────────────────────────
    {
      title: "Audit Trail",
      description: "Tool invocation logs, export schedules, and retention policy.",
      href: "/admin/audit",
      icon: <BarChart3 className="w-5 h-5" />,
      badge: hasAudit ? undefined : "Upgrade",
      hidden: !canAudit,
    },
    // ── API Keys — api_keys.manage ────────────────────────────────────────────
    {
      title: "API Keys",
      description: "Create and revoke developer API keys for programmatic admin API access.",
      href: "/api-keys",
      icon: <Code2 className="w-5 h-5" />,
      hidden: !canApiKeys,
    },
    // ── Webhooks — webhooks.manage ────────────────────────────────────────────
    {
      title: "Webhooks",
      description: "Manage outbound webhook endpoints and delivery configuration.",
      href: "/integrations",
      icon: <Key className="w-5 h-5" />,
      hidden: !canWebhooks && !canIntegrations,
    },
    // ── Workforce — groups.manage ─────────────────────────────────────────────
    {
      title: "Groups & Workforce",
      description: "Manage user groups, departments, and role assignments.",
      href: "/settings/groups",
      icon: <Sliders className="w-5 h-5" />,
      hidden: !canGroups,
    },
    // ── Super-admin — admin.access + branding.manage ─────────────────────────
    {
      title: "Platform Admin",
      description: "Tenant management, platform-wide settings, and operator tools.",
      href: "/admin",
      icon: <Globe className="w-5 h-5" />,
      badge: "Super admin",
      hidden: !canAdmin,
    },
    {
      title: "Platform Branding",
      description: "Override platform-wide branding applied across all tenants.",
      href: "/admin/branding",
      icon: <Palette className="w-5 h-5" />,
      badge: "Super admin",
      hidden: !canBranding,
    },
    // ── Notifications (always visible) ────────────────────────────────────────
    {
      title: "Notifications",
      description: "Configure personal notification preferences and delivery channels.",
      href: "/profile",
      icon: <Bell className="w-5 h-5" />,
    },
  ].filter((t) => !t.hidden);

  return (
    <PageShell
      title="Settings"
      subtitle="All administrative settings for your organisation. Tiles are shown based on your plan and role."
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <Link key={tile.href + tile.title} href={tile.href}>
            <Card className="h-full cursor-pointer hover:border-primary/50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 text-primary">
                    {tile.icon}
                    <CardTitle className="text-base">{tile.title}</CardTitle>
                  </div>
                  {tile.badge && (
                    <Badge variant={tile.badge === "Super admin" ? "secondary" : "outline"} className="text-xs shrink-0">
                      {tile.badge}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{tile.description}</CardDescription>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}
